
import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Grid, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Textbox, Datebox, Form, Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons, Drawer } from "capitalrx-components-2.0/lib/general";

export interface FilterProps {
  isOpen: boolean;
  handleCancel: () => void;
  onFilterCountChange?: (n: number) => void;
  onApplyFilters?: (filters: Record<string, any>) => void;
}

const claimStatusOptions = [
  { label: "In Progress", value: "in_progress" },
  { label: "Approved", value: "approved" },
  { label: "Partially Approved", value: "partially_approved" },
  { label: "Denied", value: "denied" },
];

const paymentStatusOptions = [
  { label: "Cleared", value: "cleared" },
  { label: "Issued", value: "issued" },
];

// Expand same-day range to full day
function normalizeEqualDayRange(start?: any, end?: any) {
  if (!start || !end) return { start, end };
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return { start, end };

  if (s.toDateString() === e.toDateString()) {
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);
  }
  return { start: s, end: e };
}


export const Filter: FC<FilterProps> = ({
  isOpen,
  handleCancel,
  onFilterCountChange,
  onApplyFilters,
}) => {
  const form = useForm();
  const [formKey, setFormKey] = useState(0);
  const today = useMemo(() => new Date(), []);

  const checkDate = (val: any) => (val ? val : null);
  const isFuture = (d: Date | null) => !!d && d > today;
  const isAfter = (a: Date | null, b: Date | null) => !!a && !!b && a > b;


  const hardReset = () => {
    form.reset({});
    form.clearErrors();
    setFormKey((k) => k + 1);
    onFilterCountChange?.(0);
    onApplyFilters?.({});
  };

  const onReset = () => {
    hardReset();
    handleCancel();
  };

  const onClear = () => {
    hardReset();
    handleCancel();
  };

  const onApply = () => {
    const vals = form.getValues();
    form.clearErrors();

    const sFrom = checkDate(vals.service_start_from);
    const sTo = checkDate(vals.service_start_to);
    const subFrom = checkDate(vals.submission_date_from);
    const subTo = checkDate(vals.submission_date_to);
    const issFrom = checkDate(vals.issued_date_from);
    const issTo = checkDate(vals.issued_date_to);

    const errors: Record<string, string> = {};

    // future checks
    if (isFuture(sFrom)) errors.service_start_from = "Date cannot be in the future";
    if (isFuture(sTo)) errors.service_start_to = "Date cannot be in the future";
    if (isFuture(subFrom)) errors.submission_date_from = "Date cannot be in the future";
    if (isFuture(subTo)) errors.submission_date_to = "Date cannot be in the future";
    if (isFuture(issFrom)) errors.issued_date_from = "Date cannot be in the future";
    if (isFuture(issTo)) errors.issued_date_to = "Date cannot be in the future";

    // range checks
    if (isAfter(sFrom, sTo)) errors.service_start_to = "End date must be after start date";
    if (isAfter(subFrom, subTo)) errors.submission_date_to = "End date must be after start date";
    if (isAfter(issFrom, issTo)) errors.issued_date_to = "End date must be after start date";

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, message]) =>
        form.setError(field, { type: "manual", message }),
      );
      return;
    }

    const { start: nsFrom, end: nsTo } = normalizeEqualDayRange(sFrom, sTo);
    const { start: nSubFrom, end: nSubTo } = normalizeEqualDayRange(subFrom, subTo);
    const { start: nIssFrom, end: nIssTo } = normalizeEqualDayRange(issFrom, issTo);

    const normalized: Record<string, any> = {
      ...vals,
      service_start_from: nsFrom ?? vals.service_start_from,
      service_start_to: nsTo ?? vals.service_start_to,
      submission_date_from: nSubFrom ?? vals.submission_date_from,
      submission_date_to: nSubTo ?? vals.submission_date_to,
      issued_date_from: nIssFrom ?? vals.issued_date_from,
      issued_date_to: nIssTo ?? vals.issued_date_to,
    };

    const apiFilters: Record<string, any> = {};

    // member/patient fields
    if (normalized.external_member_id)
      apiFilters.external_member_id = normalized.external_member_id;

    if (normalized.patient_name)
      apiFilters.patient_name = normalized.patient_name;

    // claim status (single dropdown)
    if (normalized.claim_status)
      apiFilters.claim_status = normalized.claim_status;

    // payment status → flags
    if (normalized.payment_status === "issued") {
      apiFilters.payment_is_void = false;
    } else if (normalized.payment_status === "cleared") {
      apiFilters.payment_is_cleared = true;
    }

    // date ranges
    if (normalized.service_start_from)
      apiFilters.start_date_from = normalized.service_start_from;
    if (normalized.service_start_to)
      apiFilters.start_date_to = normalized.service_start_to;

    if (normalized.submission_date_from)
      apiFilters.submission_date_from = normalized.submission_date_from;
    if (normalized.submission_date_to)
      apiFilters.submission_date_to = normalized.submission_date_to;

    if (normalized.issued_date_from)
      apiFilters.issued_date_from = normalized.issued_date_from;
    if (normalized.issued_date_to)
      apiFilters.issued_date_to = normalized.issued_date_to;

    onFilterCountChange?.(Object.keys(apiFilters).length);
    onApplyFilters?.(apiFilters);

    handleCancel();
  };

  return (
    <Drawer
      visible={isOpen}
      onClose={onReset}
      title="Filter"
      footer={
        <Shelf spacing="small">
          <Button appearance={Buttons.Functional.Tertiary.Large} onClick={onReset}>
            Reset
          </Button>
          <Shelf.Spacer />
          <Button appearance={Buttons.Functional.Secondary.Large} onClick={onClear}>
            Clear
          </Button>
          <Button appearance={Buttons.Functional.Primary.Large} onClick={onApply}>
            Filter
          </Button>
        </Shelf>
      }
    >
      <Form key={formKey} form={form}>
        <Grid>
          <Grid.Span columns={12}>
            <Textbox.Field name="external_member_id" label="Member ID" />
          </Grid.Span>

          <Grid.Span columns={12}>
            <Textbox.Field name="patient_name" label="Patient Name" />
          </Grid.Span>

          <Grid.Span columns={6}>
            <Datebox.Field name="service_start_from" label="Service Start From" zone="local" />
          </Grid.Span>
          <Grid.Span columns={6}>
            <Datebox.Field name="service_start_to" label="Service Start To" zone="local" />
          </Grid.Span>

          <Grid.Span columns={6}>
            <Datebox.Field name="submission_date_from" label="Submission Date From" zone="local" />
          </Grid.Span>
          <Grid.Span columns={6}>
            <Datebox.Field name="submission_date_to" label="Submission Date To" zone="local" />
          </Grid.Span>

          <Grid.Span columns={6}>
            <Datebox.Field name="issued_date_from" label="Payment Issued From" zone="local" />
          </Grid.Span>
          <Grid.Span columns={6}>
            <Datebox.Field name="issued_date_to" label="Payment Issued To" zone="local" />
          </Grid.Span>

          <Grid.Span columns={12}>
            <Dropdown.Field name="claim_status" label="Claim Status">
              {claimStatusOptions.map((opt) => (
                <Dropdown.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Dropdown.Option>
              ))}
            </Dropdown.Field>
          </Grid.Span>

          <Grid.Span columns={12}>
            <Dropdown.Field name="payment_status" label="Payment Status">
              {paymentStatusOptions.map((opt) => (
                <Dropdown.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Dropdown.Option>
              ))}
            </Dropdown.Field>
          </Grid.Span>
        </Grid>
      </Form>
    </Drawer>
  );
};
